import React, { useEffect, useState } from "react";
import "./DrugAnalytics.css";
import Sidebar from "../Sidebar/Sidebar";
import api from "../../api"; // ✅ use centralized axios instance
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import { FaArrowUp, FaArrowDown, FaClock } from "react-icons/fa";

const DrugAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchDrugAnalytics = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/pharmacies/pharmacies/drug-analytics`);

      // ✅ Combine drug_name + brand_name for display
      const formatDrugs = (drugs) =>
        drugs.map((d) => ({
          ...d,
          name: `${d.drug_name} (${d.brand_name})`,
        }));

      setData({
        top_drugs: formatDrugs(res.data.top_drugs || []),
        least_drugs: formatDrugs(res.data.least_drugs || []),
        expiring_drugs: res.data.expiring_drugs || [],
      });
      setError("");
    } catch (err) {
      console.error("❌ Drug analytics fetch failed:", err);
      setError("❌ Failed to load drug analytics.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrugAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="drug-analytics-layout">
        <Sidebar />
        <div className="drug-analytics-container">
          <h2>📊 Drug Analytics</h2>
          <p>Loading data...</p>
        </div>
      </div>
    );
  }

  const topDrugs = data?.top_drugs || [];
  const leastDrugs = data?.least_drugs || [];
  const expiringDrugs = data?.expiring_drugs || [];

  return (
    <div className="drug-analytics-layout">
      <Sidebar />
      <div className="drug-analytics-container">
        <h2>📊 Drug Analytics</h2>
        {error && <div className="drug-error-box">{error}</div>}

        {/* ✅ Top Drugs */}
        <div className="drug-chart-card">
          <h3>
            <FaArrowUp className="drug-card-icon purple" /> Top 5 Stocked Drugs
          </h3>
          {topDrugs.length === 0 ? (
            <p>No data available</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topDrugs}>
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="total_quantity"
                  fill="#4F46E5"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* ✅ Least Drugs */}
        <div className="drug-chart-card">
          <h3>
            <FaArrowDown className="drug-card-icon orange" /> Least 5 Stocked Drugs
          </h3>
          {leastDrugs.length === 0 ? (
            <p>No data available</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={leastDrugs}>
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="total_quantity"
                  fill="#F59E0B"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* ✅ Expiring Drugs */}
        <div className="drug-chart-card">
          <h3>
            <FaClock className="drug-card-icon red" /> About-to-Expire Drugs
          </h3>
          {expiringDrugs.length === 0 ? (
            <p>No drugs expiring within 30 days.</p>
          ) : (
            <table className="drug-analytics-table">
              <thead>
                <tr>
                  <th>Drug</th>
                  <th>Expiration Date</th>
                  <th>Quantity</th>
                </tr>
              </thead>
              <tbody>
                {expiringDrugs.map((drug, idx) => (
                  <tr key={idx}>
                    <td>{`${drug.drug_name} (${drug.brand_name})`}</td>
                    <td>{new Date(drug.expiration_date).toLocaleDateString()}</td>
                    <td>{drug.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default DrugAnalytics;
